// CosmicUKTab.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Moon } from "lucide-react";
import AstriaUKV2Shell from "../AstriaUKV2Shell";
import { useUKV2Colors } from "../AstriaUKV2ThemeContext";
import {
  AstriaUKV2ResultCard,
  UKV2LoadingState,
  UKV2ErrorState,
  UKV2ChatCta,
} from "../AstriaUKV2Card";
import { UKV2TextInput, UKV2SubmitButton } from "../AstriaUKV2Wizard";
import { useAstriaUKV2Tab } from "../useAstriaUKV2Tab";
import { AstriaUKV2CosmicData, Message } from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "Cosmic UK";

// Colors must be read INSIDE AstriaUKV2Shell's subtree (see the note in
// ZodiacPersonalityTab.tsx) — these small components are rendered as
// `children`, not evaluated inline, so their hook calls resolve after the
// shell's theme provider has mounted.

const ResultView: React.FC<{
  data?: AstriaUKV2CosmicData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { t } = useTranslation();
  const { colors } = useUKV2Colors();

  if (data) {
    return (
      <>
        <AstriaUKV2ResultCard
          icon={<Moon className="w-5 h-5" />}
          title={t("ukv2.tabs.cosmicUK.label")}
          subtitle={t("ukv2.cosmicUK.subtitle")}
          shareText={data.reading}
        >
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.75,
              color: colors.bodyColor,
              whiteSpace: "pre-wrap",
            }}
          >
            {data.reading}
          </p>
        </AstriaUKV2ResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          {t("ukv2.common.startNewReading")}
        </button>
        <UKV2ChatCta onClick={goToChat} />
      </>
    );
  }

  if (message) {
    return (
      <AstriaUKV2ResultCard icon={<Moon className="w-5 h-5" />} title={t("ukv2.tabs.cosmicUK.label")}>
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaUKV2ResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  dob: string;
  setDob: (v: string) => void;
  time: string;
  setTime: (v: string) => void;
  place: string;
  setPlace: (v: string) => void;
  onSubmit: () => void;
}> = ({ dob, setDob, time, setTime, place, setPlace, onSubmit }) => {
  const { t } = useTranslation();
  const { colors } = useUKV2Colors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          {t("ukv2.tabs.cosmicUK.label")}
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          {t("ukv2.cosmicUK.intro")}
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
        <UKV2TextInput value={dob} onChange={setDob} placeholder={t("ukv2.common.dobPlaceholder")} />
        <UKV2TextInput value={time} onChange={setTime} placeholder={t("ukv2.common.timePlaceholder")} />
        <UKV2TextInput value={place} onChange={setPlace} placeholder={t("ukv2.common.placePlaceholder")} />
      </div>

      <UKV2SubmitButton onClick={onSubmit} disabled={!dob.trim()} label={t("ukv2.cosmicUK.submit")} />
    </>
  );
};

const CosmicUKTab: React.FC = () => {
  const { t } = useTranslation();
  const { loading, error, message, send, reset, goToChat } = useAstriaUKV2Tab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [dob, setDob] = useState("");
  const [time, setTime] = useState("");
  const [place, setPlace] = useState("");
  const started = !!message || loading || !!error;

  const handleSubmit = () =>
    send(
      `Give me my Cosmic UK reading for today.
My Date of Birth: ${dob.trim()}.
My Time of Birth: ${time.trim() || "Not provided"}.
My Place of Birth: ${place.trim() || "Not provided"}.`.trim(),
    );

  const data = message?.astriaUKV2Data as AstriaUKV2CosmicData | undefined;

  if (started) {
    return (
      <AstriaUKV2Shell active="cosmic-uk">
        {loading && <UKV2LoadingState label={t("ukv2.loading.cosmicUK")} />}
        {!loading && error && <UKV2ErrorState message={error} onRetry={handleSubmit} />}
        {!loading && !error && (
          <ResultView data={data} message={message} reset={reset} goToChat={goToChat} />
        )}
      </AstriaUKV2Shell>
    );
  }

  return (
    <AstriaUKV2Shell active="cosmic-uk">
      <IntroForm
        dob={dob}
        setDob={setDob}
        time={time}
        setTime={setTime}
        place={place}
        setPlace={setPlace}
        onSubmit={handleSubmit}
      />
    </AstriaUKV2Shell>
  );
};

export default CosmicUKTab;
